// Mengen: Ganzzahlen in Tausendstel der Basiseinheit. Eine Flasche = 1000, 1,250 kg = 1250.
// Gebinde tragen `baseQty` = Basismenge je Einheit (ebenfalls in Tausendstel):
// Flasche 1000 · Kasten 20×0,5 l = 20_000 · Palette mit 40 Kästen = 800_000.
import { assertSafeInt, divRound, mulDiv, parseDecimalDe, roundHalfUp, toSafeNumber } from './decimal'

/** Menge in Tausendstel einer Einheit (Basiseinheit oder Gebinde – je nach Kontext benannt). */
export type Qty = number

export const QTY_SCALE = 1000
export const QTY_DECIMALS = 3

export function assertQty(value: number, label = 'Menge'): Qty {
  return assertSafeInt(value, label)
}

/** 1.5 → 1500. Rundet kaufmännisch auf Tausendstel. */
export function qtyFromNumber(value: number): Qty {
  return roundHalfUp(value * QTY_SCALE)
}

/** 1500 → 1.5. Nur für Anzeige/Diagramme. */
export const qtyToNumber = (qty: Qty): number => qty / QTY_SCALE

/** Ganze Stückzahl → Menge: wholeQty(12) = 12000. */
export function wholeQty(count: number): Qty {
  if (!Number.isSafeInteger(count)) throw new RangeError(`Ganze Zahl erwartet: ${count}`)
  return assertQty(count * QTY_SCALE)
}

export const isWholeQty = (qty: Qty): boolean => qty % QTY_SCALE === 0

export function sumQty(values: Iterable<Qty>): Qty {
  let total = 0
  for (const v of values) total += assertQty(v)
  return assertQty(total, 'Summe')
}

/** Deutsche Mengeneingabe → Tausendstel: '12,5 kg' → 12500, '12*20' → 240000. Ungültig → null. */
export function parseQty(text: string): Qty | null {
  return parseDecimalDe(text, QTY_DECIMALS)
}

export interface Converted {
  qty: Qty
  /** false, wenn gerundet werden musste (die Umrechnung ging nicht glatt auf) */
  exact: boolean
}

/** Menge in einem Gebinde → Basismenge: toBaseQty(2000, 20_000) = 40_000 (2 Ka = 40 Fl). */
export function toBaseQty(qtyInUnit: Qty, unitBaseQty: Qty): Qty {
  assertUnitBaseQty(unitBaseQty)
  return mulDiv(qtyInUnit, unitBaseQty, QTY_SCALE, 'half-up')
}

/** Basismenge → Menge im Gebinde: fromBaseQty(40_000, 20_000) = { qty: 2000, exact: true }. */
export function fromBaseQty(baseQty: Qty, unitBaseQty: Qty): Converted {
  assertUnitBaseQty(unitBaseQty)
  assertQty(baseQty)
  const numerator = BigInt(baseQty) * BigInt(QTY_SCALE)
  const qty = toSafeNumber(divRound(numerator, BigInt(unitBaseQty), 'half-up'))
  return { qty, exact: numerator % BigInt(unitBaseQty) === 0n }
}

/** Umrechnung zwischen zwei Gebinden desselben Artikels (über die Basiseinheit). */
export function convertQty(qty: Qty, fromUnitBaseQty: Qty, toUnitBaseQty: Qty): Converted {
  assertUnitBaseQty(fromUnitBaseQty)
  assertUnitBaseQty(toUnitBaseQty)
  assertQty(qty)
  const numerator = BigInt(qty) * BigInt(fromUnitBaseQty)
  const result = toSafeNumber(divRound(numerator, BigInt(toUnitBaseQty), 'half-up'))
  return { qty: result, exact: numerator % BigInt(toUnitBaseQty) === 0n }
}

export interface PackagingUnit {
  /** Kürzel für die Anzeige: 'Pal', 'Ka', 'Fl' */
  code: string
  /** Basismenge je Einheit in Tausendstel */
  baseQty: Qty
}

export interface UnitBreakdown {
  /** ganze Gebinde je Einheit, größte zuerst; Einheiten mit 0 entfallen */
  parts: { code: string; count: number }[]
  /** Rest in Basiseinheit (Tausendstel), der in kein Gebinde ganzzahlig passt */
  rest: Qty
  negative: boolean
}

/**
 * Zerlegt eine Basismenge in ganze Gebinde: 932 Fl mit [Ka 20, Fl 1] → „46 Ka + 12 Fl“.
 * Die Einheiten werden intern nach Größe sortiert.
 */
export function breakdownQty(baseQty: Qty, units: readonly PackagingUnit[]): UnitBreakdown {
  assertQty(baseQty)
  const sorted = [...units].sort((a, b) => b.baseQty - a.baseQty)
  let rest = Math.abs(baseQty)
  const parts: { code: string; count: number }[] = []
  for (const unit of sorted) {
    assertUnitBaseQty(unit.baseQty)
    const count = Math.floor(rest / unit.baseQty)
    if (count > 0) {
      parts.push({ code: unit.code, count })
      rest -= count * unit.baseQty
    }
  }
  return { parts, rest, negative: baseQty < 0 }
}

function assertUnitBaseQty(unitBaseQty: number): void {
  if (!Number.isSafeInteger(unitBaseQty) || unitBaseQty <= 0) {
    throw new RangeError(`Die Basismenge je Einheit muss eine positive Ganzzahl sein: ${unitBaseQty}`)
  }
}
